import { toast } from "@birdwatch/ui/toast";
import { sumBy } from "lodash-es";
import RCUpload from "rc-upload";
import type {
  RcFile,
  UploadProps as RcUploadProps,
  UploadRequestOption,
} from "rc-upload/es/interface";
import {
  type PropsWithChildren,
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { env } from "@/env";
import {
  type MediaUploadEntry,
  useMediaUpload,
} from "@/features/shared/media-upload";
import {
  jobToFormAttachment,
  type UploadPick,
} from "@/features/shared/upload/_libs/job-to-form-attachment";
import {
  MAX_FILE_SIZE_UPLOAD,
  MAX_TOTAL_FILES_SIZE_UPLOAD,
} from "@/features/shared/upload/_libs/upload-file-size";
import { UPLOAD_FILE_TYPES_DEFAULT } from "@/features/shared/upload/_libs/upload-file-type-default";
import { isSupportFileUpload } from "@/features/shared/upload/_libs/utils";
import type { AttachmentUploadHandle } from "@/libs/attachment-upload-handle";
import {
  type AttachmentSource,
  type FormAttachment,
  toFormAttachments,
} from "@/libs/form-attachment";
import { createLocalPreviewUrl } from "@/libs/local-file-preview";

type UploadProps = PropsWithChildren & {
  value?: AttachmentSource[];
  onChange?: (files: FormAttachment[]) => void;
  ref?: Ref<AttachmentUploadHandle>;
  /**
   * `true` (default) uploads through the authenticated media-v3 routes; `false`
   * is the signed-out vendor-onboarding path (`/init-public` + `/final-public`).
   */
  isPrivate?: boolean;
  /**
   * The caller keeps the URL, not the attachment id (a note's image). A file the
   * media service rewrites to a new key then reports `done` only once its final
   * URL is known. Defaults to `!isPrivate`: the signed-out path is URL-based by
   * nature (nothing links an id later).
   */
  awaitFinalUrl?: boolean;
  /**
   * Whether the media service runs its AI analysis on these files. Defaults to
   * `isPrivate`: a signed-out upload's text is never published where its
   * uploader can read it, and the URL-based vendor forms wait on processing, so
   * the analysis would only hold the row at "uploading" longer.
   */
  runAiAnalysis?: boolean;
  /**
   * Name the uploads so they survive this component: a composer passes its
   * draft key, and a remount with the same key (the tab the person switched
   * away from and back to) finds every file — still uploading or done — where
   * it left them. Omit for a form whose uploads belong to one mount.
   */
  scope?: string;
  allowFileTypes?: string[];
  multiple?: boolean;
  className?: string;
  disabled?: boolean;
};

/** What the form row needs from the browser's side of an upload entry. */
function toPick(entry: MediaUploadEntry, previewUrl?: string): UploadPick {
  return {
    uid: entry.uid,
    fileName: entry.file.name,
    fileSize: entry.file.size,
    contentType: entry.file.type,
    createdAt: entry.createdAt,
    previewUrl,
  };
}

/**
 * The invisible upload trigger every attachment form field wraps. Files go to
 * MEDIA-V3 (`useMediaUpload`): init → presigned part PUTs straight to S3 → final.
 * A row turns `done` the moment the bytes land; the media service's processing
 * (renditions, AI text) is background work the form never waits on.
 *
 * Two kinds of row live side by side, in one ordered list the parent reads via
 * `onChange`: STORED rows (the `value` prop and `addAttachments`, already on the
 * server) and PICKED rows, derived from their upload entry in the media-upload
 * store — never copied, so a job's every state change (progress, the CDN
 * thumbnail arriving) flows into the form value for free, and a picked row
 * survives this component when the scope is named. The imperative handle keeps
 * the contract the fields depend on (`@/libs/attachment-upload-handle`).
 */
export function Upload({
  children,
  value,
  onChange,
  ref,
  isPrivate = true,
  awaitFinalUrl = !isPrivate,
  runAiAnalysis = isPrivate,
  scope,
  allowFileTypes = UPLOAD_FILE_TYPES_DEFAULT,
  multiple = true,
  className,
  disabled,
}: UploadProps) {
  const rcUploadRef = useRef<any>(null);
  const { entries, upload, remove, retry } = useMediaUpload({ scope });
  const [stored, setStored] = useState<FormAttachment[]>(() =>
    toFormAttachments(value),
  );
  // Local previews by uid — the photo itself, or a poster frame drawn off the
  // video — painted under the progress ring until the CDN rendition exists.
  // Minting is async (a video poster takes a decode), so a tick re-derives rows.
  const previews = useRef(new Map<string, string>());
  const previewAttempted = useRef(new Set<string>());
  const [previewTick, setPreviewTick] = useState(0);

  const rows = useMemo<FormAttachment[]>(() => {
    // A restored draft holds the done rows as STORED; the scope may still hold
    // the entries they came from. One row per attachment.
    const storedIds = new Set(stored.map((row) => row.id));
    const picked = entries.flatMap((entry) => {
      const { job } = entry;
      if (job.attachmentId && storedIds.has(job.attachmentId)) return [];
      const previewUrl = previews.current.get(entry.uid);
      return [
        jobToFormAttachment(job, toPick(entry, previewUrl), { awaitFinalUrl }),
      ];
    });
    return [...stored, ...picked];
    // previewTick re-reads the preview map after an async mint.
  }, [stored, entries, awaitFinalUrl, previewTick]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Every picked entry gets one preview attempt — including entries this mount
  // inherited from its scope, whose previews died with the previous mount.
  useEffect(() => {
    for (const entry of entries) {
      if (previewAttempted.current.has(entry.uid)) continue;
      previewAttempted.current.add(entry.uid);
      if (entry.job.thumbnailUri) continue;
      void createLocalPreviewUrl(entry.file).then((url) => {
        if (!url) return;
        previews.current.set(entry.uid, url);
        setPreviewTick((tick) => tick + 1);
      });
    }
  }, [entries]);

  // Publish upward only when the rows' content changed — the parent's `onChange`
  // identity is new every render, and the mount value is what the parent gave us.
  const publishedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = JSON.stringify(rows);
    if (publishedRef.current === null) {
      publishedRef.current = key;
      return;
    }
    if (publishedRef.current === key) return;
    publishedRef.current = key;
    onChange?.(rows);
  }, [rows, onChange]);

  // A failed upload keeps its (removable, retryable) row; say why, once.
  const toastedRef = useRef(new Set<string>());
  useEffect(() => {
    for (const entry of entries) {
      const { job } = entry;
      if (job.phase !== "failed" || toastedRef.current.has(job.id)) continue;
      toastedRef.current.add(job.id);
      if (job.error === "Upload cancelled") continue;
      toast.error({
        title: `Couldn't upload ${entry.file.name}`,
        description: job.error ?? undefined,
      });
    }
  }, [entries]);

  const access = isPrivate ? "authed" : "public";

  const releasePreview = (uid: string) => {
    const url = previews.current.get(uid);
    if (!url) return;
    previews.current.delete(uid);
    if (url.startsWith("blob:")) URL.revokeObjectURL(url);
  };

  const removeEntries = (predicate: (entry: MediaUploadEntry) => boolean) => {
    const removed = entries.filter(predicate);
    if (removed.length === 0) return false;
    // Still uploading → stop sending bytes nobody will look at (a no-op once the
    // server has them; processing cannot be recalled).
    for (const entry of removed) {
      remove(entry.job.id);
      releasePreview(entry.uid);
    }
    return true;
  };

  // Previews are browser memory — release whatever is left on unmount.
  useEffect(() => {
    const map = previews.current;
    return () => {
      for (const url of map.values()) {
        if (url.startsWith("blob:")) URL.revokeObjectURL(url);
      }
      map.clear();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    reset() {
      removeEntries(() => true);
      setStored([]);
    },
    addAttachments(attachments = []) {
      const known = new Set(
        rowsRef.current.map((row) => row.id).filter((id) => id !== ""),
      );
      const added = toFormAttachments(
        attachments.filter((item) => !item.id || !known.has(item.id)),
      );
      if (added.length === 0) return;
      setStored((prev) => [...prev, ...added]);
    },
    removeAttachment(id: string) {
      if (!id) return;
      if (removeEntries((entry) => entry.uid === id)) return;
      if (removeEntries((entry) => entry.job.attachmentId === id)) return;
      setStored((prev) => prev.filter((row) => row.id !== id));
    },
    removeAttachmentByUri(uri: string) {
      if (!uri) return;
      if (removeEntries((entry) => entry.job.contentUri === uri)) return;
      setStored((prev) =>
        prev.filter(
          (row) => row.originalContentUri !== uri && row.contentUri !== uri,
        ),
      );
    },
    removeAttachmentByUid(uid: string) {
      if (!uid) return;
      if (removeEntries((entry) => entry.uid === uid)) return;
      setStored((prev) => prev.filter((row) => row.uid !== uid));
    },
    uploadFiles(files: File[]) {
      // Through rc-upload so `beforeUpload` gates these exactly like a pick.
      rcUploadRef.current?.uploader?.uploadFiles?.(files);
    },
    retryAttachment(id: string) {
      // Same bytes, same row: the entry keeps its uid and simply starts climbing again.
      const target = entries.find(
        (entry) => entry.uid === id || entry.job.attachmentId === id,
      );
      if (!target) return;
      retry(target.job.id);
    },
  }));

  const isAllowTotalFileSize = (files: RcFile[]) => {
    const totalSize = sumBy(files, (item) => item?.size);
    const totalFileList = sumBy(rowsRef.current, (item) => item?.fileSize);
    return totalSize + totalFileList <= MAX_TOTAL_FILES_SIZE_UPLOAD;
  };

  const uploadProps = {
    multiple,
    type: "drag",
    beforeUpload(file: RcFile, fileList: RcFile[]) {
      if (file.size === 0) {
        return false;
      }
      if (!isSupportFileUpload(file.name, allowFileTypes)) {
        toast.error({
          title: `Unsupported file format. Only ${allowFileTypes.join(", ")} is supported.`,
        });
        return false;
      }
      if (file.size > MAX_FILE_SIZE_UPLOAD) {
        toast.error({
          title: `The file is too large. Max size for a single file is ${env.PUBLIC_MAX_FILE_SIZE} MB.`,
        });
        return false;
      }
      if (!isAllowTotalFileSize(fileList)) {
        toast.error({
          title: `Max size for total files is ${env.PUBLIC_MAX_TOTAL_FILES_SIZE} MB.`,
        });
        return false;
      }
      return true;
    },
    // rc-upload's own request callbacks are not used: the job state lives in
    // the media-upload store, and the rows are derived from it. `file` is typed
    // as `RcFile | Blob | string` by rc-upload; a picker only ever hands over a
    // File (carrying rc-upload's `uid`, which becomes the row's key).
    customRequest({ file }: UploadRequestOption) {
      if (typeof file === "string" || !(file instanceof File)) return;
      upload([file], { access, runAiAnalysis });
    },
  } satisfies Partial<RcUploadProps>;

  return (
    <RCUpload
      {...uploadProps}
      ref={rcUploadRef}
      className={className}
      disabled={disabled}
    >
      {children}
    </RCUpload>
  );
}
